import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import InterestThreadDrawer from "@/components/InterestThreadDrawer";
import {
  MessageCircle, Loader2, ChevronLeft, Check, X, Phone, MapPin,
  MessageSquare,
} from "lucide-react";

const STATUSES = ["new", "contacted", "booked", "declined"];
const STATUS_TONE = {
  new:       "bg-amber-500/20 text-amber-200",
  contacted: "bg-sky-500/20 text-sky-200",
  booked:    "bg-emerald-500/25 text-emerald-100",
  declined:  "bg-red-500/20 text-red-200",
};
const STATUS_LABEL = {
  new: "New signal", contacted: "Contacted",
  booked: "Booked", declined: "Declined",
};

/**
 * Distributor's live interest board — every exhibitor who has raised
 * a "want to book" signal on one of the distributor's films. Grouped
 * by film, per status. Distributors update the status inline as they
 * work through each lead.
 */
export default function DistributorInterests() {
  const [loading, setLoading] = useState(true);
  const [interests, setInterests] = useState([]);
  const [counts, setCounts] = useState({ new: 0, contacted: 0, booked: 0, declined: 0 });
  const [tab, setTab] = useState("new");

  const load = (status = tab, { silent = false } = {}) => {
    if (!silent) setLoading(true);
    api.get("/distributor/interests/mine", { params: status ? { status } : {} })
      .then(({ data }) => { setInterests(data.interests || []); setCounts(data.counts || {}); })
      .catch(() => { setInterests([]); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [tab]);

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10"
         data-testid="distributor-interests-page">
      <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-white/55 hover:text-white transition"
            data-testid="distributor-interests-back">
        <ChevronLeft size={13}/> Back to Dashboard
      </Link>

      <div className="mt-6">
        <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">L13 · Distributor</div>
        <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2 text-teal-300"
            data-testid="distributor-interests-heading">
          Interest Signals
        </h1>
        <p className="mt-3 text-sm text-white/60 max-w-2xl leading-relaxed">
          Every exhibitor who&apos;s marked one of your films as &ldquo;want to
          book&rdquo; on the discovery board. Update status as you work each
          lead — new → contacted → booked (or declined).
        </p>
      </div>

      {/* Status tabs */}
      <div className="mt-8 flex flex-wrap gap-2" data-testid="distributor-interests-tabs">
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setTab(s)}
                  className={`px-4 py-2 rounded-full text-[11px] tracking-[0.22em] uppercase transition-colors ${
                    tab === s
                      ? "bg-teal-500/25 text-teal-100 border border-teal-400/40"
                      : "bg-white/5 text-white/60 border border-white/10 hover:bg-white/10"
                  }`}
                  data-testid={`distributor-interests-tab-${s}`}>
            {STATUS_LABEL[s]} · {counts[s] || 0}
          </button>
        ))}
      </div>

      {/* Results */}
      <div className="mt-8">
        {loading ? (
          <div className="cs-card rounded-xl p-8 text-center text-white/55"
               data-testid="distributor-interests-loading">
            <Loader2 size={16} className="animate-spin inline mr-2"/> Loading…
          </div>
        ) : interests.length === 0 ? (
          <div className="cs-card rounded-xl p-10 text-center"
               data-testid="distributor-interests-empty">
            <MessageCircle size={30} className="mx-auto text-white/25 mb-4"/>
            <div className="text-white/70 text-sm">No {STATUS_LABEL[tab].toLowerCase()} signals.</div>
            <div className="text-white/45 text-[11px] mt-2 max-w-md mx-auto">
              Exhibitors browsing the slate discovery board can raise
              interest on any of your films — those requests show up
              here for you to work.
            </div>
          </div>
        ) : (
          <div className="grid gap-4" data-testid="distributor-interests-list">
            {interests.map((row) => (
              <InterestRow key={row.id} row={row}
                           softRefresh={() => load(tab, { silent: true })}
                           refresh={() => load(tab)}/>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function InterestRow({ row, refresh, softRefresh }) {
  const [busy, setBusy] = useState(false);
  const [showThread, setShowThread] = useState(false);
  const setStatus = async (next) => {
    setBusy(true);
    try {
      await api.patch(`/distributor/interests/${row.id}/status`, { status: next });
      toast.success(`Marked ${next}.`);
      refresh();
    } catch { toast.error("Update failed."); } finally { setBusy(false); }
  };
  return (
    <div className="cs-card rounded-xl p-5"
         data-testid={`distributor-interest-${row.id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display text-lg uppercase tracking-tight">{row.film_title}</div>
            <span className={`px-2 py-0.5 rounded-full text-[9px] tracking-[0.18em] uppercase font-semibold ${STATUS_TONE[row.status] || "bg-white/8 text-white/60"}`}
                  data-testid={`distributor-interest-status-${row.id}`}>
              {STATUS_LABEL[row.status] || row.status}
            </span>
          </div>
          <div className="mt-1.5 text-xs text-white/60">
            <span className="text-teal-300 font-semibold">{row.exhibitor_name || "Exhibitor"}</span>
            {row.exhibitor_city && (
              <span className="ml-2 inline-flex items-center gap-1 text-white/50">
                <MapPin size={10}/> {row.exhibitor_city}{row.exhibitor_state ? `, ${row.exhibitor_state}` : ""}
              </span>
            )}
            {row.territory && (
              <span className="ml-2 text-white/50">· Wants for {row.territory}</span>
            )}
          </div>
          {row.note && (
            <div className="mt-2 text-[12px] text-white/75 italic border-l-2 border-teal-500/30 pl-3 py-0.5">
              &ldquo;{row.note}&rdquo;
            </div>
          )}
          <div className="mt-1 text-[10px] text-white/40">
            Raised · {row.created_at?.slice(0, 10) || "?"}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <button onClick={() => setShowThread(true)}
                  className="px-3 py-1.5 rounded-full bg-white/8 text-white/85 text-[10px] tracking-[0.18em] uppercase hover:bg-white/15 transition inline-flex items-center gap-1"
                  data-testid={`distributor-interest-message-${row.id}`}>
            <MessageSquare size={11}/> Message
          </button>
          {row.status !== "contacted" && (
            <button onClick={() => setStatus("contacted")} disabled={busy}
                    className="px-3 py-1.5 rounded-full bg-sky-500/20 text-sky-200 text-[10px] tracking-[0.18em] uppercase hover:bg-sky-500/30 transition disabled:opacity-50 inline-flex items-center gap-1"
                    data-testid={`distributor-interest-mark-contacted-${row.id}`}>
              <Phone size={11}/> Contacted
            </button>
          )}
          {row.status !== "booked" && (
            <button onClick={() => setStatus("booked")} disabled={busy}
                    className="px-3 py-1.5 rounded-full bg-emerald-500/25 text-emerald-100 text-[10px] tracking-[0.18em] uppercase hover:bg-emerald-500/35 transition disabled:opacity-50 inline-flex items-center gap-1"
                    data-testid={`distributor-interest-mark-booked-${row.id}`}>
              <Check size={11}/> Booked
            </button>
          )}
          {row.status !== "declined" && (
            <button onClick={() => setStatus("declined")} disabled={busy}
                    className="px-3 py-1.5 rounded-full bg-red-500/15 text-red-200 text-[10px] tracking-[0.18em] uppercase hover:bg-red-500/25 transition disabled:opacity-50 inline-flex items-center gap-1"
                    data-testid={`distributor-interest-mark-declined-${row.id}`}>
              <X size={11}/> Decline
            </button>
          )}
        </div>
      </div>
      {showThread && (
        <InterestThreadDrawer
          interest={row}
          myRole="distributor"
          onClose={() => { setShowThread(false); refresh(); }}
          onSent={softRefresh}
        />
      )}
    </div>
  );
}
